import {
  AgencyAuthLoginRequestResultV1Schema,
  AgencyAuthLoginVerifyV1Schema,
  AgencyAuthSessionV1Schema,
  type AgencyAuthSessionV1,
} from "@enrein/shared-contracts";
import type { StudioRuntimeConfig } from "./env.js";

export interface StudioAuthClient {
  readonly getSession: () => Promise<AgencyAuthSessionV1 | null>;
  readonly requestLogin: (email: string) => Promise<{ devMagicLinkToken?: string }>;
  readonly verifyLogin: (token: string) => Promise<AgencyAuthSessionV1>;
  readonly logout: () => Promise<void>;
}

export function createStudioAuthClient(
  config: StudioRuntimeConfig,
  fetchImpl: typeof fetch = fetch,
): StudioAuthClient {
  const base = config.apiBaseUrl.replace(/\/$/, "");

  async function parseJson(res: Response): Promise<unknown> {
    return res.json() as Promise<unknown>;
  }

  return {
    async getSession() {
      if (!base) {
        return null;
      }
      const res = await fetchImpl(`${base}/v1/auth/agency/session`, {
        method: "GET",
        credentials: "include",
      });
      if (res.status === 401) {
        return null;
      }
      if (!res.ok) {
        const body = (await parseJson(res)) as { error?: string };
        throw new Error(body.error ?? `studio_auth_error_${res.status}`);
      }
      return AgencyAuthSessionV1Schema.parse(await parseJson(res));
    },

    async requestLogin(email) {
      const res = await fetchImpl(`${base}/v1/auth/agency/login-request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const body = (await parseJson(res)) as Record<string, unknown>;
      if (!res.ok) {
        throw new Error(typeof body.error === "string" ? body.error : `studio_auth_error_${res.status}`);
      }
      AgencyAuthLoginRequestResultV1Schema.parse(body);
      return {
        devMagicLinkToken:
          typeof body.devMagicLinkToken === "string" ? body.devMagicLinkToken : undefined,
      };
    },

    async verifyLogin(token) {
      const res = await fetchImpl(`${base}/v1/auth/agency/login-verify`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(AgencyAuthLoginVerifyV1Schema.parse({ token })),
      });
      const body = await parseJson(res);
      if (!res.ok) {
        const maybe = body as { error?: string };
        throw new Error(maybe.error ?? `studio_auth_error_${res.status}`);
      }
      return AgencyAuthSessionV1Schema.parse(body);
    },

    async logout() {
      await fetchImpl(`${base}/v1/auth/agency/logout`, {
        method: "POST",
        credentials: "include",
      });
    },
  };
}
